import { ArtworkWrapper } from "@/types/artworksData";
import AppData from "../app.data";
import * as THREE from "three";
import { clampShadowMapSize } from "../renderer/qualityCaps";

const appData = new AppData();

/**
 * Checks if a property exists in an array of strings
 * @param propName Property name to search for
 * @param data Array of strings to search in
 * @returns Boolean indicating if property was found
 */
export function checkForPropInArray(propName: string, data: string[]) {
    let reuslt = false;
    for (let index = 0; index < data.length; index++) {
        if (data[index] === propName) {
            reuslt = true;
        }
    }
    return reuslt;
}

/**
 * Adjusts a value within a circular range
 * When value exceeds max, wraps to min, and vice versa
 * @param currentValue Current value to adjust
 * @param min Minimum value of the range
 * @param max Maximum value of the range
 * @param change Amount to adjust the value by
 * @returns Adjusted value within the specified range
 */
export function adjustValue(currentValue: number, min: number, max: number, change: number) {
    // Add the change to the currentValue
    let newValue = currentValue + change;

    // Check if newValue exceeds max
    if (newValue > max) {
        newValue = min;
    }
    // Check if newValue is lower than min
    else if (newValue < min) {
        newValue = max;
    }

    return newValue;
}

export function applyHighQualityHints() {
    if (appData.renderService.renderQuality !== "MQ" && appData.renderService.renderQuality !== "HQ") return
    const maxAniso = (appData.renderService.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
    const shadowMapSize = clampShadowMapSize(8192, appData.renderService.renderQuality);
    const scenes = [appData.spaceScene, appData.artworksScene];
    scenes.forEach((scene: any) => {
        scene?.traverse((obj: any) => {
            if (obj && obj.isLight && obj.shadow && obj.shadow.mapSize) {
                // obj.castShadow = true;
                obj.shadow.mapSize.set(shadowMapSize, shadowMapSize);
                if ('radius' in obj.shadow) obj.shadow.radius = 2;
            }
            if (obj && obj.isMesh) {
                const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
                materials.forEach((m: any) => {
                    if (!m) return;
                    if (m.map) m.map.anisotropy = Math.min(maxAniso, 8);
                });
            }
        });
    });
}

/**
 * Determines the file type from a URL
 * Supports PNG, JPG, and MP4 file types
 * @param url URL to analyze
 * @returns File type as 'png', 'jpg', 'mp4', or null if not recognized
 */
export function getFileType(url: string): 'png' | 'jpg' | 'mp4' | null {
    // The regex looks for .png, .jpg, or .mp4 followed by either a '?' (query string) or end-of-string.
    const regex = /\.(png|jpg|mp4)(?=[\?]|$)/i;
    const match = url.match(regex);
    return match ? match[1].toLowerCase() as 'png' | 'jpg' | 'mp4' : null;
}

export function sortByIndex(elements: any[]): any[] {
    if (!elements || !Array.isArray(elements)) {
        return [];
    }

    return [...elements].sort((a, b) => {
        // Handle cases where index might be missing
        const indexA = a.index !== undefined ? a.index : 0;
        const indexB = b.index !== undefined ? b.index : 0;

        return indexA - indexB;
    });
}

export function getSlotObjectBySlotTarget(slotTarget: number, appData: any) {
    const targetIndex = Number(slotTarget);
    if (!Number.isFinite(targetIndex)) return undefined;

    const slot = appData?.artworksScene?.children?.find?.((child) => {
        const childIndex = Number(child?.userData?.index);
        return Number.isFinite(childIndex) && childIndex === targetIndex;
    });
    return slot
}


export const SUPPORTED_MODEL_SOURCE_TYPES = ['glb', 'gltf', 'ply', 'stl', 'fbx', 'obj', 'dae', '3ds', 'jpg', 'jpeg', 'png', 'zip'] as const;
const MODEL_SOURCE_TYPE_SET = new Set<string>(SUPPORTED_MODEL_SOURCE_TYPES);

export type ModelSourceType = typeof SUPPORTED_MODEL_SOURCE_TYPES[number];

export interface ModelTextureSource {
    name: string;
    url: string;
}

export interface ModelSourceDescriptor {
    type: ModelSourceType;
    url: string;
    entryFile?: string;
    materialFile?: string;
    previewImage?: string;
    textureFolder?: ModelTextureSource[];
}

export type ModelLoadSource = string | ModelSourceDescriptor;

export function getModelLoadSourceUrls(source: ModelLoadSource | null | undefined): string[] {
    if (!source) return [];

    if (typeof source === "string") {
        return [source];
    }

    const urls = [source.url];

    if (source.materialFile) {
        urls.push(source.materialFile);
    }

    if (source.previewImage) {
        urls.push(source.previewImage);
    }

    if (Array.isArray(source.textureFolder)) {
        source.textureFolder.forEach((texture) => {
            if (texture?.url) {
                urls.push(texture.url);
            }
        });
    }

    return urls.filter(Boolean);
}
/**
 * Gets the file extension from a URL when it matches a supported source type
 * @param url The URL string
 * @returns The supported extension in lowercase, or null if not found
 */
export function getExtensionFromUrl(url: string): string | null {
    if (!url) return null;
    // Remove query string and hash
    const cleanUrl = url.split('?')[0].split('#')[0];
    const match = cleanUrl.match(/\.([a-zA-Z0-9]+)$/);
    if (match) {
        const ext = match[1].toLowerCase();
        if (MODEL_SOURCE_TYPE_SET.has(ext)) {
            return ext;
        }
    }
    return null;
}

export function sortByDistanceToCamera(artworkWrappers: ArtworkWrapper[], cameraPosition: THREE.Vector3): ArtworkWrapper[] {
    type ArtworkWrapperWithDistance = ArtworkWrapper & { distanceTocamera: number };
    if (!Array.isArray(artworkWrappers)) return [];

    const cam = new THREE.Vector3(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    const tmp = new THREE.Vector3();

    const withDistance = artworkWrappers.reduce<ArtworkWrapperWithDistance[]>((acc, wrapper) => {
        const slotObject = getSlotObjectBySlotTarget(wrapper?.slotTarget, appData);
        if (!slotObject || wrapper.artworks.length === 0) return acc; // Skip wrappers with no slot or no artworks

        const pos = new THREE.Vector3(
            slotObject.position.x + wrapper.transforms.position.x,
            slotObject.position.y + wrapper.transforms.position.y,
            slotObject.position.z + wrapper.transforms.position.z
        );

        const distanceTocamera = tmp.set(pos.x, pos.y, pos.z).distanceTo(cam);
        acc.push({
            ...wrapper,
            distanceTocamera,
        });
        return acc;
    }, []);

    return withDistance.sort((a, b) => (a.distanceTocamera) - (b.distanceTocamera));
}
